import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, forkJoin, map, of, switchMap, take, tap } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { NavTileComponent, NavTileStat } from '../components/nav-tile/nav-tile.component';
import { isPlatformAdmin } from '../core/auth/platform-admin.guard';
import { ConfigService } from '../core/config/config.service';
import { AccountService } from '../account/account.service';
import { CustomerService } from '../services/customer.service';
import { ProjectService } from '../services/project.service';
import { Membership } from '../models/membership.model';
import { CustomerDTO } from '../models/customer.model';
import { DashboardPageComponent } from '../live-dashboard/dashboard-page.component';
import { DashAbschnittComponent } from '../components/dash-abschnitt/dash-abschnitt.component';
import { MeineAufgabenCardComponent } from './meine-aufgaben/meine-aufgaben-card.component';
import { MeineProjekteComponent } from './meine-projekte/meine-projekte.component';

interface HomeTile {
  link: string;
  icon: string;
  titleKey?: string;
  titleText?: string;
  subtitleKey: string;
  stats: NavTileStat[];
  testId: string;
}

const PROJECT_TILE_ID = 'arbeitsbereich-kachel-projekte';

type HomeCase = 'single-project' | 'multi' | 'platform';

interface WelcomeSubtitle {
  key: string;
  params?: Record<string, unknown>;
}

@Component({
  selector: 'app-home',
  imports: [
    RouterOutlet,
    TranslatePipe,
    MatProgressSpinner,
    MatIcon,
    NavTileComponent,
    DashboardPageComponent,
    DashAbschnittComponent,
    MeineAufgabenCardComponent,
    MeineProjekteComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private configService = inject(ConfigService);
  private accountService = inject(AccountService);
  private customerService = inject(CustomerService);
  private projectService = inject(ProjectService);

  private readonly _loading = signal(true);
  readonly loading = this._loading.asReadonly();

  private readonly _loadError = signal(false);
  readonly loadError = this._loadError.asReadonly();

  private readonly _isPlatformAdmin = signal(false);
  readonly platformAdmin = this._isPlatformAdmin.asReadonly();

  private readonly _memberships = signal<Membership[]>([]);
  readonly memberships = this._memberships.asReadonly();

  private readonly _membershipsFehler = signal(false);
  readonly aufgabenSichtbar = computed(() => this._membershipsFehler() || this._memberships().length > 0);

  readonly displayName = signal<string>('');
  readonly projectCount = signal<number | null>(null);
  readonly adminProjectCount = signal<number | null>(null);
  readonly customerCount = signal<number | null>(null);
  readonly activeCustomerCount = signal<number | null>(null);

  readonly homeCase = computed<HomeCase>(() => {
    if (this._isPlatformAdmin()) return 'platform';
    const memberships = this._memberships();
    if (memberships.length === 1 && memberships[0].role !== 'PROJECT_ADMIN') return 'single-project';
    return 'multi';
  });

  readonly singleProject = computed(() => {
    const memberships = this._memberships();
    return this.homeCase() === 'single-project' && memberships.length === 1 ? memberships[0] : null;
  });

  readonly firstName = computed(() => (this.displayName() ?? '').trim().split(/\s+/)[0]);

  readonly welcomeSubtitle = computed<WelcomeSubtitle>(() => {
    const memberships = this._memberships();
    if (memberships.length === 1) {
      return { key: 'home.welcome.singleProject', params: { name: memberships[0].projectName } };
    }
    if (memberships.length > 1) return { key: 'home.welcome.subtitle', params: { count: memberships.length } };
    return { key: this._isPlatformAdmin() ? 'home.welcome.allProjects' : 'home.welcome.noProject' };
  });

  readonly tiles = computed<HomeTile[]>(() => {
    const single = this.singleProject();
    if (single) {
      return [
        {
          link: `/projects/${single.projectId}`,
          icon: 'folder',
          titleText: single.projectName,
          subtitleKey: 'home.tiles.singleProject.subtitle',
          stats: [],
          testId: PROJECT_TILE_ID,
        },
      ];
    }
    const tiles: HomeTile[] = [
      {
        link: '/dashboard',
        icon: 'bar_chart',
        titleKey: 'home.tiles.dashboard.title',
        subtitleKey: 'home.tiles.dashboard.subtitle',
        stats: this.stats([
          ['home.tiles.stats.projects', this.projectCount()],
          ['home.tiles.stats.admin', this.adminProjectCount()],
        ]),
        testId: PROJECT_TILE_ID,
      },
    ];
    if (this._isPlatformAdmin()) {
      tiles.push({
        link: '/client-management',
        icon: 'group',
        titleKey: 'home.tiles.clientManagement.title',
        subtitleKey: 'home.tiles.clientManagement.subtitle',
        stats: this.stats([
          ['home.tiles.stats.customers', this.customerCount()],
          ['home.tiles.stats.active', this.activeCustomerCount()],
        ]),
        testId: 'arbeitsbereich-kachel-kunden',
      });
    }
    return tiles;
  });

  readonly tileGridClass = computed(() =>
    this.tiles().length <= 1 ? 'home__tile-grid home__tile-grid--single' : 'home__tile-grid',
  );

  ngOnInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        map(() => isPlatformAdmin(this.msalService, this.configService.azure.tenantId)),
        tap((platformAdmin) => this._isPlatformAdmin.set(platformAdmin)),
        switchMap((platformAdmin) =>
          forkJoin({
            profile: this.accountService.getProfile(),
            memberships: this.accountService.getMemberships().pipe(catchError(() => of<Membership[] | null>(null))),
            visibleProjectCount: this.projectService.list().pipe(
              map((projects) => projects.filter((p) => p.status !== 'archiviert').length),
              catchError(() => of<number | null>(null)),
            ),
            customers: platformAdmin
              ? this.customerService.getCustomers().pipe(catchError(() => of<CustomerDTO[] | null>(null)))
              : of<CustomerDTO[] | null>(null),
          }),
        ),
      )
      .subscribe({
        next: ({ profile, memberships, visibleProjectCount, customers }) => {
          this.displayName.set(profile.displayName);
          const liste = memberships ?? [];
          this._memberships.set(liste);
          this._membershipsFehler.set(memberships === null);
          this.projectCount.set(visibleProjectCount ?? liste.length);
          this.adminProjectCount.set(liste.filter((m) => m.role === 'PROJECT_ADMIN').length);
          if (customers !== null) {
            this.customerCount.set(customers.length);
            this.activeCustomerCount.set(customers.filter((c) => c.status === 'ACTIVE').length);
          }
          this._loading.set(false);
        },
        error: () => {
          this._loadError.set(true);
          this._loading.set(false);
        },
      });
  }

  private stats(entries: [labelKey: string, value: number | null][]): NavTileStat[] {
    const shown = entries.flatMap(([labelKey, value]) => (value === null ? [] : [{ labelKey, value }]));
    return shown.length === entries.length ? shown : [];
  }
}
